import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ThumbsUp, ThumbsDown, Users, UserPlus } from 'lucide-react'
import TeamAvatar from './TeamAvatar'
import TeamHonors from './TeamHonors'
import { accentColor } from '../utils/teamColors'
import { CARD_CLASS } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'

/**
 * Карточка банды в каталоге.
 * Кнопка «Подать заявку» рисуется только при `recruiting: true` — в выдаче
 * поиска (`q`) встречаются и ненабирающие банды, у них кнопки быть не должно.
 * `hasPendingApplication` — активная заявка в эту банду уже подана: кнопка
 * неактивна, с текстом «Заявка отправлена».
 */
const TeamCatalogCard = ({ team, canApply, hasPendingApplication = false, onApply, onOpen }) => {
  const { t } = useTranslation()

  const accent = accentColor(team.colors)
  const isFull = team.members_count >= team.max_members
  // В каталоге чип показываем только для достигнутого уровня выше нулевого
  const level = Number.isFinite(team.level) && team.level > 0 ? team.level : null
  // Потолок у каждой банды свой (админ меняет вручную) — «x/y» только из ответа
  const maxMembers = Number.isFinite(team.max_members) ? team.max_members : null

  const openTeam = () => {
    hapticImpact('light')
    onOpen(team)
  }

  return (
    <div
      onClick={openTeam}
      className={`${CARD_CLASS} relative block w-full cursor-pointer overflow-hidden p-4 text-left transition-all active:scale-[0.98] lg:flex lg:flex-col`}
    >
      {/* Мягкое свечение цвета банды в углу карточки */}
      <div
        className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full blur-2xl"
        style={{ backgroundColor: `${accent}1F` }}
      />

      <div className="relative flex items-start gap-3">
        <span className="relative block shrink-0">
          <TeamAvatar team={team} size={56} />
          {/* Онлайн-индикатор: показываем только активную банду */}
          {team.online_count > 0 && (
            <span
              className="absolute -right-1 -top-1 block h-3.5 w-3.5 rounded-full border-2 border-dark-primary"
              style={{ backgroundColor: accent, boxShadow: `0 0 8px ${accent}99` }}
            />
          )}
          {level !== null && (
            <span
              className="absolute -bottom-1.5 left-1/2 flex -translate-x-1/2 items-center gap-0.5 whitespace-nowrap rounded-full border bg-surface px-1.5 py-0.5"
              style={{ borderColor: accent, boxShadow: `0 0 8px ${accent}44` }}
              title={t('team.level', 'Уровень банды')}
            >
              <span className="text-[8px] font-bold uppercase leading-none tracking-wide text-text-secondary">
                {t('publicProfile.levelShort', 'LVL')}
              </span>
              <span className="text-[10px] font-bold leading-none tabular-nums" style={{ color: accent }}>
                {level}
              </span>
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-base font-bold not-italic text-accent-primary">
              {team.name}
            </h3>
            {/* Лайки и дизлайки показываем только ненулевые */}
            {(team.likes > 0 || team.dislikes > 0) && (
              <div className="flex shrink-0 items-center gap-2 text-xs text-text-secondary">
                {team.likes > 0 && (
                  <span className="flex items-center gap-1">
                    <ThumbsUp size={12} className="text-accent-primary" />
                    <span className="tabular-nums">{team.likes}</span>
                  </span>
                )}
                {team.dislikes > 0 && (
                  <span className="flex items-center gap-1">
                    <ThumbsDown size={12} className="text-red-400" />
                    <span className="tabular-nums">{team.dislikes}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {team.recruit_text && (
            <p className="mt-1 whitespace-pre-line break-words text-xs text-text-secondary">{team.recruit_text}</p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5 text-white">
              <Users size={13} style={{ color: accent }} />
              <span className="tabular-nums">
                {team.members_count ?? 0}{maxMembers !== null ? `/${maxMembers}` : ''}
              </span>
            </span>
          </div>

          <div className="mt-2"><TeamHonors team={team} /></div>
        </div>
      </div>

      {/* Заявку можно подать только в набирающую банду со свободным местом; при активной заявке — серая неактивная кнопка */}
      {team.recruiting && canApply && (
        <div className="relative mt-3 lg:mt-auto lg:pt-3">
          {hasPendingApplication ? (
            <button
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm font-semibold text-text-secondary"
            >
              <Check size={15} />
              {t('team.applicationSent', 'Заявка отправлена')}
            </button>
          ) : (
            <button
              onClick={(event) => {
                event.stopPropagation()
                hapticImpact('medium')
                onApply(team)
              }}
              disabled={isFull}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-4 py-2.5 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98] disabled:opacity-50"
            >
              <UserPlus size={15} />
              {isFull
                ? t('team.full', 'Мест нет')
                : t('team.apply', 'Подать заявку')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default TeamCatalogCard
